/**
 * Turns a raw quote payload into the labelled rows shown in the watchlist
 * hover box.
 *
 * The complaint this answers: a bare "+0.54%" and "+$1" beside a portfolio is
 * ambiguous — clients could not tell whether the percentage was for the day or
 * the year, what the dollar figure counted, or what the instrument was
 * currently worth. Every row here therefore says what period it covers and
 * what unit it is in.
 *
 * Pure on purpose: the formatting rules are the thing worth testing, and they
 * should not need a DOM to exercise.
 */

/**
 * Strict numeric coercion.
 *
 * `Number(null)` is 0 and `Number('')` is 0, both of which are finite — so a
 * plain `Number.isFinite(Number(x))` guard renders a missing price as "$0.00"
 * and happily computes a range position for a feed that returned nothing.
 * Absent has to stay distinguishable from zero.
 */
function toFiniteNumber(value) {
  if (value === null || value === undefined || value === '') return null;
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
}

/**
 * Index levels and futures are quoted in points; stocks and ETFs are quoted in
 * money. The kinds come from the server's quote payloads — `futures` (plural)
 * is what barchartMarketDataService emits, and `etf` is deliberately absent
 * here because SPY and QQQ really do trade in dollars per share.
 */
export function isPointsQuoted(kind) {
  return kind === 'index' || kind === 'futures';
}

export function formatQuotePrice(value, { prefix = '$', points = false } = {}) {
  const num = toFiniteNumber(value);
  if (num === null) return '—';
  const body = Math.abs(num).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const sign = num < 0 ? '-' : '';
  return points ? `${sign}${body}` : `${sign}${prefix}${body}`;
}

export function formatQuoteSigned(value, { prefix = '$', points = false } = {}) {
  const num = toFiniteNumber(value);
  if (num === null) return '—';
  const body = Math.abs(num).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const sign = num < 0 ? '-' : '+';
  return points ? `${sign}${body}` : `${sign}${prefix}${body}`;
}

export function formatQuotePercent(value) {
  const num = toFiniteNumber(value);
  if (num === null) return '—';
  return `${num >= 0 ? '+' : '-'}${Math.abs(num).toFixed(2)}%`;
}

/** Volume is read at a glance, so it gets a magnitude suffix rather than 12 digits. */
export function formatQuoteVolume(value) {
  const num = toFiniteNumber(value);
  if (num === null || num < 0) return '—';
  if (num >= 1e9) return `${(num / 1e9).toFixed(2)}B`;
  if (num >= 1e6) return `${(num / 1e6).toFixed(2)}M`;
  if (num >= 1e3) return `${(num / 1e3).toFixed(1)}K`;
  return String(Math.round(num));
}

function formatRange(low, high, opts) {
  const lo = toFiniteNumber(low);
  const hi = toFiniteNumber(high);
  if (lo === null || hi === null) return null;
  return `${formatQuotePrice(lo, opts)} – ${formatQuotePrice(hi, opts)}`;
}

/**
 * Where the last price sits between the low and the high, 0–100.
 * Returns null when the range is degenerate, so callers never divide by zero
 * or draw a marker for a range that has not moved.
 */
export function rangePosition(low, high, last) {
  const lo = toFiniteNumber(low);
  const hi = toFiniteNumber(high);
  const value = toFiniteNumber(last);
  if (lo === null || hi === null || value === null || hi <= lo) return null;
  const pct = ((value - lo) / (hi - lo)) * 100;
  return Math.min(100, Math.max(0, pct));
}

export function formatQuoteTimestamp(iso) {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * @returns {{rows: Array<{label: string, value: string}>, ranges: Array, footnote: string, asOf: string|null}}
 */
export function buildQuoteDetails(quote, { kind, currencyPrefix } = {}) {
  const resolvedKind = kind || quote?.kind || 'stock';
  const points = isPointsQuoted(resolvedKind);
  const prefix = currencyPrefix || quote?.currencyPrefix || '$';
  const opts = { prefix, points };

  const unit = points ? 'points' : 'per share';
  const noun = points ? 'Level' : 'Price';

  const rows = [
    { label: noun, value: formatQuotePrice(quote?.lastPrice ?? quote?.close, opts) },
    {
      label: 'Change since previous close',
      value: `${formatQuoteSigned(quote?.netChange, opts)} (${formatQuotePercent(quote?.percentChange)})`,
    },
    { label: 'Previous close', value: formatQuotePrice(quote?.previousClose, opts) },
  ];

  if (toFiniteNumber(quote?.open) !== null) {
    rows.push({ label: "Today's open", value: formatQuotePrice(quote.open, opts) });
  }
  // Cash indexes report volume 0 (there is nothing to trade), and a "Volume 0"
  // row reads as a data error rather than as a property of the instrument.
  if ((toFiniteNumber(quote?.volume) ?? 0) > 0) {
    rows.push({ label: 'Volume', value: formatQuoteVolume(quote.volume) });
  }

  const ranges = [];
  const dayRange = formatRange(quote?.dayLow ?? quote?.low, quote?.dayHigh ?? quote?.high, opts);
  if (dayRange) {
    ranges.push({
      key: 'day',
      label: "Today's range",
      value: dayRange,
      position: rangePosition(
        quote?.dayLow ?? quote?.low,
        quote?.dayHigh ?? quote?.high,
        quote?.lastPrice ?? quote?.close,
      ),
    });
  }
  const yearRange = formatRange(quote?.fiftyTwoWeekLow, quote?.fiftyTwoWeekHigh, opts);
  if (yearRange) {
    ranges.push({
      key: 'year',
      label: '52-week range',
      value: yearRange,
      position: rangePosition(
        quote?.fiftyTwoWeekLow,
        quote?.fiftyTwoWeekHigh,
        quote?.lastPrice ?? quote?.close,
      ),
    });
  }

  const footnote = points
    ? `Change is today's move since the previous close, measured in index ${unit} — `
      + 'not dollars, and not related to your account.'
    : `Change is today's move since the previous close, ${unit} — `
      + 'not a yearly return, and not money in your account.';

  return {
    rows,
    ranges,
    footnote,
    asOf: formatQuoteTimestamp(quote?.tradeTimestamp),
    delayed: quote?.mode !== 'realtime',
  };
}
